import isEmpty from 'lodash/isEmpty'
import {exhaustive} from '@freckle/exhaustive'
import {fromMaybe} from '@freckle/maybe'
import {
  toHTMLTextDangerous,
  innerHTMLText
} from '@freckle/educator-entities/ts/common/models/html-text'
import type {MathQuestionBucketMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-bucket'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {
  buckets,
  bucket,
  answersContainer,
  bucketAnswer,
  bucketNameContainer
} from './bucket-answer.module.scss'

export type BucketCorrectAnswertFormatT = 'text' | 'list' | 'non-interactive-ui'

type Props = {
  metadata: MathQuestionBucketMetadataAttrs
  format: BucketCorrectAnswertFormatT
}

export const BucketAnswer = ({metadata, format}: Props) => {
  const {bucketFieldsName, bucketCorrectAnswers} = metadata
  const {t} = useSafeTranslation('common')

  const joined = bucketFieldsName.map((field, i) => {
    const answer = fromMaybe(() => [], bucketCorrectAnswers[i])
    const joined = isEmpty(answer)
      ? t('BUCKET_FIELD_EMPTY')
      : answer.join(` ${t('BUCKET_FIELD_SEPARATOR')} `)
    return `${field}: ${joined}`
  })

  switch (format) {
    case 'text':
      return <span {...innerHTMLText(toHTMLTextDangerous(joined.join('\n')))} />
    case 'list':
      const items = joined.map((ans, i) => (
        <li key={i} {...innerHTMLText(toHTMLTextDangerous(ans))} />
      ))
      return <ul>{items}</ul>
    case 'non-interactive-ui':
      return <NonInteractiveBucketAnswers metadata={metadata} />
    default:
      return exhaustive(format)
  }
}

function NonInteractiveBucketAnswers({metadata}: {metadata: MathQuestionBucketMetadataAttrs}) {
  const {bucketFieldsName, bucketCorrectAnswers} = metadata

  return (
    <div className={buckets}>
      {bucketFieldsName.map((bucketName, i: number) => (
        <div key={i} className={bucket}>
          <div className={answersContainer}>
            {bucketCorrectAnswers[i]?.map((answer, j) => (
              <div key={j} className={bucketAnswer}>
                {answer}
              </div>
            ))}
          </div>
          <div className={bucketNameContainer}>{bucketName}</div>
        </div>
      ))}
    </div>
  )
}
